/**
 * Helpers para armar el mensaje de WhatsApp pre-armado y la URL wa.me.
 */
import { parseTime, formatDate } from "./configDefaults";

export const cleanPhoneForWa = (raw) => (raw || "").replace(/\D/g, "");

/* True en teléfonos/tablets. Decide qué tipo de link de WhatsApp emitimos:
 * en mobile el esquema nativo `whatsapp://` (abre la app instalada, incluido
 * WhatsApp Business); en desktop la web `https://wa.me/` (abre WhatsApp Web).
 * Ver waLink() para el por qué. Inyectable por si hay que forzarlo en tests. */
export const isMobileWaDevice = (ua) => {
  const s = ua ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
  return /android|iphone|ipad|ipod|iemobile|blackberry|opera mini|mobile/i.test(s || "");
};

/* Construye el link de WhatsApp a un número ya limpio (E.164 sin "+") con texto
 * ya codificado. Elige el formato según el dispositivo:
 *
 *  - MOBILE → `whatsapp://send?phone=...&text=...` (esquema nativo).
 *    `wa.me` es una web que INTENTA redirigir a la app; cuando esa redirección no
 *    dispara (usuario con SOLO WhatsApp Business, o link abierto desde un navegador
 *    in-app tipo Instagram/Facebook) cae en la web de WhatsApp, que solo ofrece
 *    descargar el WhatsApp NORMAL — nunca abre el Business. El esquema `whatsapp://`
 *    lo registran AMBAS apps, así que abre la que esté instalada (o muestra un
 *    selector en Android si están las dos).
 *
 *  - DESKTOP → `https://wa.me/...`. En desktop `whatsapp://` requiere WhatsApp
 *    Desktop instalado y falla si no está; `wa.me` abre WhatsApp Web. Las pantallas
 *    del dueño (Hoy, Clientes, Espera) se usan seguido desde una notebook, así que
 *    conservamos el fallback web ahí. */
const waLink = (clean, enc) =>
  isMobileWaDevice()
    ? `whatsapp://send?phone=${clean}&text=${enc}`
    : `https://wa.me/${clean}?text=${enc}`;

/* Normaliza un teléfono para wa.me. Si el teléfono no tiene + ni 00 al
 * inicio, le agregamos el código de país del restaurante (heurística AR/UY/BR).
 * Sin esto, "12345678" abre wa.me/12345678 que es un número aleatorio. */
// AR: WhatsApp exige el 9 de móviles (formato 54 9 área número). Toma un string de
// SOLO dígitos y devuelve "549" + número nacional (área + abonado = 10 díg). Limpia
// el 54 de país, el 0 troncal, el 9 de móvil ya puesto (no lo duplica) y el "15" de
// celular que va DESPUÉS del código de área.
const arWithMobile9 = (d) => {
  let local = d.startsWith("54") ? d.slice(2) : d;
  if (local.startsWith("0")) local = local.slice(1); // 0 troncal de larga distancia
  if (local.startsWith("9")) local = local.slice(1); // 9 de móvil (re-normalizamos)
  // El "15" de celular: sin él, el número nacional es 10 díg; con él, 12. El código
  // de área es 2 díg (CABA/GBA = 11) o 3-4 (interior). Buscamos el "15" justo
  // después del área y lo sacamos. Las áreas de 3-4 díg nunca empiezan en "11", así
  // que no hay falso positivo con CABA. Solo actuamos a 12 díg (señal inequívoca de 15).
  if (local.length === 12) {
    for (const areaLen of [2, 3, 4]) {
      if (local.slice(areaLen, areaLen + 2) === "15") {
        local = local.slice(0, areaLen) + local.slice(areaLen + 2);
        break;
      }
    }
  }
  return "549" + local;
};

/* Normaliza el WhatsApp del PROPIO LOCAL a formato wa.me (E.164 sin "+").
 * Asume Argentina por defecto (la gran mayoría de los clientes); UY/BR deben venir
 * con su código de país. Acepta cualquier forma en que un dueño tipea su número:
 * con/sin 0 troncal, con/sin 15, con/sin +54, con/sin el 9 de móvil, con espacios,
 * guiones o paréntesis. "1157463566" → "5491157463566". Devuelve "" si no hay dígitos.
 *
 * Crítico: el número del local nunca se normalizaba (solo el del comensal), así que
 * "1157463566" generaba "wa.me/1157463566" y WhatsApp lo leía como +1 (EE.UU.). */
export const normalizeRestaurantWa = (raw) => {
  let d = cleanPhoneForWa(raw);
  if (!d) return "";
  if (d.startsWith("00")) d = d.slice(2); // prefijo de salida internacional
  if (d.startsWith("598")) return d; // Uruguay (ya trae su código de país)
  if (d.startsWith("55") && d.length >= 12) return d; // Brasil (móvil con 9)
  return arWithMobile9(d); // Argentina (default)
};

/* True si `raw` se normaliza a un WhatsApp de local plausible. Para AR exige el
 * formato móvil completo 54 9 + 10 díg (= 13). UY/BR: que tengan largo razonable.
 * Reemplaza la vieja validación "≥10 dígitos" que dejaba pasar números rotos. */
export const isValidWaNumber = (raw) => {
  const n = normalizeRestaurantWa(raw);
  if (!n) return false;
  if (n.startsWith("549")) return n.length === 13; // AR móvil
  if (n.startsWith("598")) return n.length >= 11; // UY
  if (n.startsWith("55")) return n.length >= 12; // BR
  return n.length >= 11;
};

export const normalizePhoneForWa = (raw, restaurantWaNumber) => {
  if (!raw) return "";
  const digits = cleanPhoneForWa(raw);
  if (!digits) return "";
  const hasIntlPrefix = raw.trim().startsWith("+") || raw.trim().startsWith("00");
  if (hasIntlPrefix) {
    // +54 sin el 9 también queda roto para móviles → lo insertamos.
    if (digits.startsWith("54")) return arWithMobile9(digits);
    return digits;
  }
  const restDigits = cleanPhoneForWa(restaurantWaNumber);
  if (!restDigits) return digits;
  if (restDigits.startsWith("598")) return "598" + digits; // UY
  if (restDigits.startsWith("55")) return "55" + digits;   // BR
  if (restDigits.startsWith("54")) return arWithMobile9(digits); // AR (con el 9)
  return digits;
};

/* Construye el link de WhatsApp con texto codificado (formato según dispositivo,
 * ver waLink). restaurantWaNumber es opcional: si lo pasás, normalizamos el phone
 * del cliente con el código de país del restaurant cuando no tiene prefijo. */
export const buildWaMeUrl = (phone, text, restaurantWaNumber) => {
  const clean = restaurantWaNumber
    ? normalizePhoneForWa(phone, restaurantWaNumber)
    : cleanPhoneForWa(phone);
  if (!clean) return null;
  return waLink(clean, encodeURIComponent(text || ""));
};

/* Link de WhatsApp HACIA el del LOCAL (reserva entrante, consulta, cancelación).
 * Normaliza con normalizeRestaurantWa (asume AR) para que ande aunque el número
 * guardado esté crudo. Usar SIEMPRE para links al local; buildWaMeUrl (con 3er arg)
 * es para links al COMENSAL. Formato según dispositivo (ver waLink): en mobile abre
 * también WhatsApp Business, no cae en la web que solo ofrece el WhatsApp normal. */
export const buildWaMeUrlToRestaurant = (restaurantWaNumber, text) => {
  const clean = normalizeRestaurantWa(restaurantWaNumber);
  if (!clean) return null;
  return waLink(clean, encodeURIComponent(text || ""));
};

/* Valida que una hora HH:MM caiga dentro de al menos uno de los rangos
 * abiertos. Soporta rangos que cruzan medianoche (end < start). */
export const isTimeInRanges = (timeStr, ranges) => {
  if (!timeStr || !Array.isArray(ranges) || ranges.length === 0) return false;
  const t = parseTime(timeStr);
  return ranges.some((r) => {
    const rs = parseTime(r.start);
    let re = parseTime(r.end);
    let tt = t;
    if (re <= rs) {
      re += 24 * 60;
      if (tt < rs) tt += 24 * 60;
    }
    return tt >= rs && tt < re;
  });
};

/* Detecta el idioma esperado por el restaurante a partir del código de país
 * del WhatsApp. AR(54)/UY(598) → es, BR(55) → pt. Default es. */
export const detectRestaurantLocale = (waNumber) => {
  const d = cleanPhoneForWa(waNumber);
  if (d.startsWith("55") && !d.startsWith("598")) return "pt";
  // Default es para AR/UY y todo lo demás. NO inferimos "en" del prefijo "1":
  // un móvil AR escrito con el prefijo "15" sin código de país (ej "11 15...")
  // empieza en 1 y caía en inglés. config.locale tiene precedencia si se setea.
  return "es";
};

const MSG_I18N = {
  es: {
    header: "📅 RESERVA",
    name: "Nombre",
    date: "Fecha",
    time: "Horario",
    people: "Personas",
    table: "Mesa",
    zone: "Zona",
    note: "Nota",
    timeJoin: "a",
    ask: "¿Me confirmás si está disponible? Gracias 🙌",
  },
  en: {
    header: "📅 RESERVATION",
    name: "Name",
    date: "Date",
    time: "Time",
    people: "Party",
    table: "Table",
    zone: "Zone",
    note: "Note",
    timeJoin: "to",
    ask: "Can you confirm if it's available? Thanks 🙌",
  },
  pt: {
    header: "📅 RESERVA",
    name: "Nome",
    date: "Data",
    time: "Horário",
    people: "Pessoas",
    table: "Mesa",
    zone: "Zona",
    note: "Observação",
    timeJoin: "às",
    ask: "Pode me confirmar se está disponível? Obrigado(a) 🙌",
  },
};

/* Texto del mensaje que el cliente envía al restaurante.
 * Estructurado para parseo futuro y legible para humanos.
 *
 * mode === "free" → solo mostramos la hora de inicio (el end existe internamente
 * para overlap detection pero el cliente no se compromete a quedarse hasta él).
 * mode === "turns" → mostramos "21:00 a 23:00" porque es el turno completo.
 *
 * locale define el idioma del mensaje y del formato de fecha. Default es. */
export const buildBookingMessage = ({
  customerName,
  date,
  turn,
  people,
  tableNumber,
  tableLabel,
  zone,
  notes,
  restaurantName,
  mode,
  locale = "es",
}) => {
  const L = MSG_I18N[locale] || MSG_I18N.es;
  const lines = [];
  lines.push(L.header);
  if (restaurantName) lines.push(`(${restaurantName})`);
  lines.push("");
  lines.push(`${L.name}: ${customerName}`);
  lines.push(`${L.date}: ${formatHumanDate(date, locale)}`);
  if (mode === "free") {
    if (turn?.start) lines.push(`${L.time}: ${turn.start}`);
  } else if (turn?.start && turn?.end) {
    lines.push(`${L.time}: ${turn.start} ${L.timeJoin} ${turn.end}`);
  } else if (turn?.start) {
    lines.push(`${L.time}: ${turn.start}`);
  }
  lines.push(`${L.people}: ${people}`);

  // Zona (modo zonas): el cliente pide lugar en una zona puntual.
  if (zone) lines.push(`${L.zone}: ${zone}`);

  // Línea de mesa: priorizar el número (lo que identifica), luego la zona.
  let mesaLine = "";
  if (tableNumber && tableLabel) mesaLine = `${L.table}: #${tableNumber} (${tableLabel})`;
  else if (tableNumber) mesaLine = `${L.table}: #${tableNumber}`;
  else if (tableLabel) mesaLine = `${L.table}: ${tableLabel}`;
  if (mesaLine) lines.push(mesaLine);

  if (notes) {
    lines.push("");
    lines.push(`${L.note}: ${notes}`);
  }
  lines.push("");
  lines.push(L.ask);
  return lines.join("\n");
};

/* Reemplaza variables {name}, {date}, {time}, {people}, {tableName} en plantilla */
export const fillTemplate = (template, vars) => {
  if (!template) return "";
  return template.replace(/\{(name|date|time|people|tableName|manageLink)\}/g, (_, key) => {
    if (key === "name") return vars.customerName || "";
    if (key === "date") return formatHumanDate(vars.date);
    if (key === "time") return vars.turn?.start || "";
    if (key === "people") return String(vars.people || "");
    if (key === "tableName") return vars.tableLabel || "";
    if (key === "manageLink") return vars.manageLink || "";
    return "";
  });
};

/* Formato del date string YYYY-MM-DD a "Sáb 15 jun" / "Sat Jun 15" / etc.
 * Locale opcional: default es-AR (compat con AR/UY donde está la mayor parte
 * de los clientes); para BR pasar "pt-BR", para EN "en-US". */
const LOCALE_TO_BCP47 = { es: "es-AR", en: "en-US", pt: "pt-BR" };
export function formatHumanDate(str, locale = "es") {
  if (!str) return "";
  const [y, m, d] = String(str).split("-").map(Number);
  const dateObj = new Date(y, m - 1, d);
  const bcp = LOCALE_TO_BCP47[locale] || locale || "es-AR";
  try {
    return dateObj.toLocaleDateString(bcp, {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  } catch {
    return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
  }
}

/* Devuelve los turnos disponibles para una fecha (YYYY-MM-DD)
 * según la config (mode + daysOfWeek + blockedDates).
 * En modo "free" devolvemos [] — el cliente elige hora libremente vía time picker. */
const DAY_KEYMAP = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/* Config EFECTIVA de una fecha puntual (YYYY-MM-DD), resolviendo en orden de
 * prioridad: (1) excepción por fecha en `dateOverrides` — open:true abre un día
 * que normalmente cerrás (feriados); open:false cierra uno que normalmente abrís;
 * (2) `blockedDates` → cerrado; (3) el día de la semana en `daysOfWeek`.
 * Devuelve { enabled, turns, timeMode } (+ flags de origen). Resolver ÚNICO:
 * TODOS los pickers (público y del dueño) pasan por acá para no divergir.
 * `dateOverrides` es un ARRAY de { date, open, timeMode?, turns? } (no un mapa: así
 * Firestore reemplaza la lista entera al guardar y los borrados sí persisten). */
export function effectiveDayConfig(config, dateStr) {
  const fallbackMode = config?.timeMode || config?.mode || "free";
  if (!config || !dateStr) return { enabled: false, turns: [], timeMode: fallbackMode };

  // Defensivo: aunque normalizeConfig lo coacciona a [], blindamos el resolver por
  // si recibe un config crudo (un mapa legacy haría .find no-es-función → crash).
  const overrides = Array.isArray(config.dateOverrides) ? config.dateOverrides : [];
  const ov = overrides.find((o) => o && o.date === dateStr);
  if (ov) {
    const timeMode = ov.timeMode || fallbackMode;
    if (!ov.open) return { enabled: false, turns: [], timeMode, isOverride: true, open: false };
    return { enabled: true, turns: ov.turns || [], timeMode, isOverride: true, open: true };
  }

  if (config.blockedDates?.includes(dateStr)) {
    return { enabled: false, turns: [], timeMode: fallbackMode, blocked: true };
  }

  const [y, m, d] = String(dateStr).split("-").map(Number);
  const dayKey = DAY_KEYMAP[new Date(y, m - 1, d).getDay()];
  const day = config.daysOfWeek?.[dayKey] || { enabled: false, turns: [] };
  return {
    enabled: !!day.enabled,
    turns: day.turns || [],
    timeMode: day.timeMode || fallbackMode,
  };
}

/* timeMode efectivo PARA UNA FECHA (free/turns), vía resolver central.
 * Permite "L-V libre, V/S por turnos" + overrides por fecha. */
export function timeModeForDate(config, dateStr) {
  if (!config || !dateStr) return config?.timeMode || config?.mode || "free";
  return effectiveDayConfig(config, dateStr).timeMode;
}

/* Turnos disponibles para una fecha (modo "turns"). En modo "free" devuelve []
 * (el cliente elige la hora libremente). Respeta overrides/bloqueos. */
export function turnsForDate(config, dateStr) {
  if (!config || !dateStr) return [];
  const eff = effectiveDayConfig(config, dateStr);
  if (!eff.enabled) return [];
  if (eff.timeMode === "free") return [];
  return (eff.turns || []).filter(
    (t) => parseTime(t.end) > parseTime(t.start) || parseTime(t.end) < parseTime(t.start)
  );
}

/* Rangos de apertura VÁLIDOS de una fecha (modo "free"): ventanas en las que se
 * puede reservar. Respeta overrides/bloqueos y descarta rangos invertidos.
 * Helper compartido por el widget público y los pickers del dueño. */
export function openRangesForDate(config, dateStr) {
  const eff = effectiveDayConfig(config, dateStr);
  if (!eff.enabled) return [];
  return validOpenRanges(eff.turns);
}

/* Suma minutos a un string "HH:MM", devuelve nuevo "HH:MM" (24h, sin wrap de día). */
export function addMinutesToTime(timeStr, mins) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  let total = h * 60 + m + (mins || 0);
  total = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const hh = String(Math.floor(total / 60)).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

/* Construye un turn a partir de hora de inicio + duración config. */
export function buildFreeTurn(startTime, durationMinutes = 120) {
  if (!startTime) return null;
  return {
    start: startTime,
    end: addMinutesToTime(startTime, durationMinutes),
  };
}

/* ── Día de servicio (servicio que cruza medianoche) ──
 *
 * Un restaurante abierto de noche sigue en el MISMO servicio después de las
 * 12: una cena 22:30-00:30 y una reserva 00:00-01:00 son la misma noche. Para
 * comparar/ordenar/agrupar correctamente usamos un "día de servicio" con una
 * hora de corte (rollover): las horas ANTERIORES al corte (madrugada) cuentan
 * como la noche del día anterior.
 *
 * Default 6:00 — seguro para AR/UY/BR: ningún turno de cena termina después de
 * las 6 ni empieza antes. Override por restaurante vía config.dayRolloverHour. */
export const DAY_ROLLOVER_HOUR = 6;

/* Minutos "lineales" dentro del día de servicio. Las horas < corte se corren
 * +24h, de modo que 00:30 (1470) queda DESPUÉS de 22:30 (1350) y no antes. */
export function serviceMinutes(timeStr, rolloverHour = DAY_ROLLOVER_HOUR) {
  const m = parseTime(timeStr);
  return m < rolloverHour * 60 ? m + 24 * 60 : m;
}

/* Rangos de apertura VÁLIDOS de un día: descarta los invertidos (end <= start en
 * el marco de servicio), que son un typo del dueño ("22:00→20:00"). mergeRanges
 * los expandiría a ~24h y ofrecería slots todo el día. El cruce de medianoche
 * legítimo (20:00→02:00, 23:30→00:30) tiene end > start en service-minutes y se
 * conserva. Helper compartido entre el widget público y los pickers del dueño
 * (antes el filtro vivía solo en el path público → divergían). */
export function validOpenRanges(turns = [], rolloverHour = DAY_ROLLOVER_HOUR) {
  return (turns || []).filter(
    (r) => r?.start && r?.end && serviceMinutes(r.end, rolloverHour) > serviceMinutes(r.start, rolloverHour)
  );
}

/* Devuelve true si dos turnos se solapan en el tiempo, interpretando ambos en
 * el marco del día de servicio (las horas de madrugada pertenecen a la noche
 * anterior). Así un turno que cruza medianoche solapa con una reserva temprana
 * del mismo servicio. */
export function turnsOverlap(a, b, rolloverHour = DAY_ROLLOVER_HOUR) {
  if (!a?.start || !a?.end || !b?.start || !b?.end) return false;
  const aStart = serviceMinutes(a.start, rolloverHour);
  let aEnd = serviceMinutes(a.end, rolloverHour);
  const bStart = serviceMinutes(b.start, rolloverHour);
  let bEnd = serviceMinutes(b.end, rolloverHour);
  if (aEnd <= aStart) aEnd += 24 * 60; // turno largo que termina pasado el corte
  if (bEnd <= bStart) bEnd += 24 * 60;
  return Math.max(aStart, bStart) < Math.min(aEnd, bEnd);
}

/* Comparator para ordenar bookings por hora de inicio dentro del día de
 * servicio (22:30 va antes que 00:30). Sin hora → al final. */
export function compareTurnStart(a, b, rolloverHour = DAY_ROLLOVER_HOUR) {
  const va = a?.turn?.start ? serviceMinutes(a.turn.start, rolloverHour) : Infinity;
  const vb = b?.turn?.start ? serviceMinutes(b.turn.start, rolloverHour) : Infinity;
  return va - vb;
}

/* Fecha (YYYY-MM-DD) del día de servicio en curso para un instante dado. Antes
 * del corte devuelve el día anterior (seguimos en la noche de ayer). Usa la
 * hora local del dispositivo; para multi-huso ver config.timezone (pendiente). */
export function getServiceDate(now = new Date(), rolloverHour = DAY_ROLLOVER_HOUR) {
  const d = new Date(now.getTime());
  if (d.getHours() < rolloverHour) d.setDate(d.getDate() - 1);
  return formatDate(d);
}

/* Minuto de servicio mínimo a partir del cual una reserva es válida HOY:
 * ahora + anticipación mínima (config.minAdvanceMinutes). Para fechas FUTURAS
 * devuelve -Infinity (todo el horario disponible). Con minAdvance=0 igual filtra
 * los horarios que ya pasaron hoy. `now` es inyectable para tests. */
export function earliestBookableMinutes(
  date,
  minAdvanceMinutes = 0,
  now = new Date(),
  rolloverHour = DAY_ROLLOVER_HOUR
) {
  if (!date || date !== getServiceDate(now, rolloverHour)) return -Infinity;
  const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  return serviceMinutes(hhmm, rolloverHour) + (Number(minAdvanceMinutes) || 0);
}

const _hhmm = (m) => {
  const mm = ((m % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(mm / 60)).padStart(2, "0")}:${String(mm % 60).padStart(2, "0")}`;
};

/* Fusiona rangos de apertura contiguos o solapados en ventanas máximas (en el
 * marco del día de servicio). Ej: [{12-14},{14-16}] → [{12-16}]. Así una reserva
 * tardía puede extenderse a través de rangos pegados en vez de cortarse. */
export function mergeRanges(ranges = [], rolloverHour = DAY_ROLLOVER_HOUR) {
  const norm = (ranges || [])
    .filter((r) => r?.start && r?.end)
    .map((r) => {
      const s = serviceMinutes(r.start, rolloverHour);
      let e = serviceMinutes(r.end, rolloverHour);
      if (e <= s) e += 24 * 60;
      return { s, e };
    })
    .sort((a, b) => a.s - b.s);
  const merged = [];
  for (const r of norm) {
    const last = merged[merged.length - 1];
    if (last && r.s <= last.e) last.e = Math.max(last.e, r.e);
    else merged.push({ ...r });
  }
  return merged.map((r) => ({ start: _hhmm(r.s), end: _hhmm(r.e) }));
}

/* Horas de inicio (cada stepMin) dentro de los rangos (fusionados), dejando al
 * menos minTailMinutes libres antes del cierre (para no ofrecer un inicio pegado
 * al cierre). La reserva tardía dura menos (se recorta con capTurnEnd). */
export function slotsFromRanges(ranges = [], stepMin = 30, minTailMinutes = 0, rolloverHour = DAY_ROLLOVER_HOUR) {
  const out = [];
  const seen = new Set();
  mergeRanges(ranges, rolloverHour).forEach((r) => {
    const start = serviceMinutes(r.start, rolloverHour);
    let end = serviceMinutes(r.end, rolloverHour);
    if (end <= start) end += 24 * 60;
    const lastStart = end - (minTailMinutes || 0);
    for (let tt = start; tt <= lastStart; tt += stepMin) {
      const label = _hhmm(tt);
      if (!seen.has(label)) {
        seen.add(label);
        out.push(label);
      }
    }
  });
  return out;
}

/* Fin EFECTIVO de una reserva: start + duración, pero recortado al cierre del
 * rango que contiene al inicio (la mesa no se ocupa pasado el cierre). Así una
 * reserva tardía simplemente dura menos en vez de quedar bloqueada. */
export function capTurnEnd(startStr, durationMinutes, ranges = [], rolloverHour = DAY_ROLLOVER_HOUR) {
  const s = serviceMinutes(startStr, rolloverHour);
  const desiredEnd = s + (durationMinutes || 0);
  let cap = desiredEnd;
  mergeRanges(ranges, rolloverHour).forEach((r) => {
    const rs = serviceMinutes(r.start, rolloverHour);
    let re = serviceMinutes(r.end, rolloverHour);
    if (re <= rs) re += 24 * 60;
    if (s >= rs && s < re) cap = Math.min(desiredEnd, re);
  });
  return _hhmm(cap);
}

/* True si el turno ENTERO (start→end) entra dentro de algún rango de apertura
 * (la reserva termina antes o justo en el cierre). Consciente del día de
 * servicio para los turnos/rangos que cruzan medianoche. */
export function turnFitsInRanges(turn, ranges, rolloverHour = DAY_ROLLOVER_HOUR) {
  if (!turn?.start || !turn?.end || !Array.isArray(ranges)) return false;
  const ts = serviceMinutes(turn.start, rolloverHour);
  let te = serviceMinutes(turn.end, rolloverHour);
  if (te <= ts) te += 24 * 60;
  return ranges.some((r) => {
    if (!r?.start || !r?.end) return false;
    const rs = serviceMinutes(r.start, rolloverHour);
    let re = serviceMinutes(r.end, rolloverHour);
    if (re <= rs) re += 24 * 60;
    return ts >= rs && te <= re;
  });
}

/* Minutos de margen entre dos turnos de la MISMA mesa. Positivo = hueco entre
 * una reserva y la otra; 0 = pegadas; negativo = se solapan. Orden-independiente,
 * consciente del día de servicio. Sirve para avisar "turnaround ajustado". */
export function turnsGap(a, b, rolloverHour = DAY_ROLLOVER_HOUR) {
  if (!a?.start || !a?.end || !b?.start || !b?.end) return Infinity;
  const aS = serviceMinutes(a.start, rolloverHour);
  let aE = serviceMinutes(a.end, rolloverHour);
  if (aE <= aS) aE += 24 * 60;
  const bS = serviceMinutes(b.start, rolloverHour);
  let bE = serviceMinutes(b.end, rolloverHour);
  if (bE <= bS) bE += 24 * 60;
  return aS <= bS ? bS - aE : aS - bE;
}

export { formatDate };
